import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Hash, Radio } from "lucide-react";

import { Card } from "../../components/Card";
import { StateBadge } from "../../components/StateBadge";
import { PageHeader } from "../../components/ui/PageHeader";
import { formatAbsolute, formatRelative, formatTooltip } from "../../lib/datetime";
import { ApiError } from "../../lib/api";
import { useCall } from "../../hooks/useCall";
import { AudioScrubber } from "./AudioScrubber";
import { EvidencePanel } from "./EvidencePanel";
import { HaltButton } from "./HaltButton";
import { Recording } from "./Recording";
import { TurnRow } from "./TurnRow";
import { TechnicalPanel } from "./TechnicalPanel";

export function LiveCallDetail() {
  const { id } = useParams();
  const { data, isLoading, error, failureCount } = useCall(id);

  // Lifted state — AudioScrubber jumps the audio by setting currentTime
  // on this ref, and reveals the Recording panel if it isn't already.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [recordingRevealed, setRecordingRevealed] = useState(false);
  const [pendingJumpSec, setPendingJumpSec] = useState<number | null>(null);

  useEffect(() => {
    if (pendingJumpSec === null) return;
    const el = audioRef.current;
    if (!el) return;
    const apply = () => {
      el.currentTime = pendingJumpSec;
      el.play().catch(() => {});
      setPendingJumpSec(null);
    };
    if (el.readyState >= 1) {
      apply();
    } else {
      el.addEventListener("loadedmetadata", apply, { once: true });
      return () => el.removeEventListener("loadedmetadata", apply);
    }
  }, [pendingJumpSec, recordingRevealed]);

  if (!id) return null;
  // A 404 while a freshly-dispatched call is still dialing is expected and
  // transient — the worker writes the call row at call-start, after the SIP
  // dial begins. Surface it as a "connecting" state (not a red error) whether
  // we're mid-retry (isLoading + ≥1 failed attempt) or the retry budget is
  // exhausted. The MQTT call/{id}/state subscription will flip the view live
  // the moment the worker publishes call_started.
  const connecting =
    (isLoading && failureCount > 0) || (error instanceof ApiError && error.status === 404);
  if (connecting) return <StatusBlock label="Connecting — waiting for the call to start…" />;
  if (isLoading) return <StatusBlock label="Acquiring call detail…" />;
  if (error) return <StatusBlock label={String(error)} tone="error" />;
  if (!data) return null;

  const rec = data.record;
  const live = rec.state === "LIVE";

  const handleJumpToTurn = (offsetSeconds: number) => {
    if (!recordingRevealed) setRecordingRevealed(true);
    setPendingJumpSec(offsetSeconds);
  };

  return (
    <div style={{ fontFamily: "var(--font-sans)" }}>
      <PageHeader
        eyebrow={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <Link
              to="/live-calls"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "var(--text-muted)",
                textDecoration: "none",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.18em",
              }}
            >
              <ArrowLeft size={11} strokeWidth={1.5} />
              LIVE CALLS
            </Link>
            <span style={{ color: "var(--border-strong)" }}>/</span>
            <span style={{ color: "var(--text-secondary)" }}>{rec.call_id.slice(0, 8)}</span>
          </span>
        }
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 16 }}>
            <span>
              CALL{" "}
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                  color: "var(--text-secondary)",
                }}
              >
                {rec.call_id.slice(0, 8)}
              </span>
            </span>
            <StateBadge kind={live ? "live" : "clean"}>
              {live ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Radio size={10} strokeWidth={2} />
                  LIVE
                </span>
              ) : (
                rec.state
              )}
            </StateBadge>
          </span>
        }
        meta={
          <span>
            <span title={formatTooltip(rec.started_at)}>
              <span style={{ color: "var(--text-secondary)" }}>
                {formatAbsolute(rec.started_at)}
              </span>{" "}
              <span style={{ color: "var(--text-muted)" }}>
                · {formatRelative(rec.started_at)}
              </span>
            </span>
            <Dot />
            Turn <span style={{ color: "var(--text-secondary)" }}>{rec.turn_count}</span>
            <Dot />
            Rejects{" "}
            <span style={{ color: rec.rejection_count ? "var(--warning)" : "var(--text-secondary)" }}>
              {rec.rejection_count}
            </span>
            {rec.termination_reason ? (
              <>
                <Dot />
                Ended{" "}
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    color: "var(--accent-bright)",
                    padding: "1px 6px",
                    border: "1px solid var(--accent-line)",
                    background: "var(--accent-subtle)",
                    borderRadius: 3,
                    letterSpacing: "0.04em",
                  }}
                  title={rec.termination_reason}
                >
                  {formatTerminationReason(rec.termination_reason)}
                </span>
              </>
            ) : null}
            {rec.sip_trunk_label ? (
              <>
                <Dot />
                Trunk{" "}
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    color: "var(--text-secondary)",
                    padding: "1px 6px",
                    border: "1px solid var(--border)",
                    background: "var(--bg-tertiary)",
                    borderRadius: 3,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                  title={`SIP carrier: ${rec.sip_trunk_label}`}
                >
                  {rec.sip_trunk_label}
                </span>
              </>
            ) : null}
            <Dot />
            <span style={{ color: "var(--text-muted)" }}>{rec.call_id}</span>
          </span>
        }
        actions={live ? <HaltButton callId={rec.call_id} liveCall={rec} /> : null}
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 280px",
          gap: 18,
          alignItems: "start",
        }}
      >
        {/* Left column — transcript + audio */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <AudioScrubber
            turns={data.turns}
            callStartedAt={rec.started_at}
            onJumpToTurn={handleJumpToTurn}
            disabled={live}
          />
          <Recording
            callId={rec.call_id}
            callState={rec.state}
            revealed={recordingRevealed}
            onReveal={() => setRecordingRevealed(true)}
            audioRef={audioRef}
          />

          <EvidencePanel callId={rec.call_id} callState={rec.state} />

          <div
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-card)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "11px 18px",
                background: "var(--bg-secondary)",
                borderBottom: "1px solid var(--border-subtle)",
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                textTransform: "uppercase",
                letterSpacing: "0.16em",
                color: "var(--text-muted)",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  TRANSCRIPT
              </span>
              <span>{data.turns.length} TURNS</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {data.turns.length === 0 ? (
                <div
                  style={{
                    padding: "40px 24px",
                    color: "var(--text-muted)",
                    textAlign: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  Awaiting first turn…
                </div>
              ) : (
                data.turns.map((t) => <TurnRow key={t.turn_id} turn={t} />)
              )}
            </div>
          </div>
        </div>

        {/* Right column — at-a-glance metrics */}
        <aside style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SidePanel
            eyebrow="Latency"
            rows={[
              { label: "TURNS", value: String(rec.turn_count) },
              { label: "REJECTS", value: String(rec.rejection_count), tone: rec.rejection_count ? "warn" : "muted" },
            ]}
          />
          <SidePanel
            eyebrow="Audit chain"
            icon={<Hash size={11} strokeWidth={1.5} />}
            rows={[
              {
                label: "LAST SEQ",
                value: data.last_audit_seq != null ? `#${data.last_audit_seq}` : "—",
                mono: true,
                tone: data.last_audit_seq != null ? undefined : "muted",
                title: data.last_audit_seq != null
                  ? undefined
                  : "No audit-chain entries yet for this call",
              },
              {
                label: "ROOT HASH",
                value: data.audit_chain_root_hash?.slice(0, 8) ?? "—",
                mono: true,
                tone: data.audit_chain_root_hash ? undefined : "muted",
                title: data.audit_chain_root_hash
                  ? undefined
                  : live
                    ? "Chain still in-flight — root hash seals when call ends"
                    : "No audit-chain entries yet for this call",
              },
            ]}
            accent="var(--cta)"
          />
          <TechnicalPanel rec={rec} turns={data.turns} />
        </aside>
      </div>
    </div>
  );
}

function SidePanel({
  eyebrow,
  icon,
  rows,
  accent = "var(--accent)",
}: {
  eyebrow: string;
  icon?: React.ReactNode;
  rows: { label: string; value: string; tone?: "warn" | "muted"; mono?: boolean; title?: string }[];
  accent?: string;
}) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderLeft: `2px solid ${accent}`,
        borderRadius: "var(--radius-card)",
        padding: "14px 16px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          paddingBottom: 10,
          borderBottom: "1px solid var(--border-subtle)",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          letterSpacing: "0.18em",
          color: "var(--text-muted)",
          textTransform: "uppercase",
        }}
      >
        <span style={{ color: accent }}>{icon}</span>
        {eyebrow}
      </div>
      <div style={{ display: "flex", flexDirection: "column", marginTop: 10 }}>
        {rows.map((r) => {
          const color =
            r.tone === "warn"
              ? "var(--warning)"
              : r.tone === "muted"
                ? "var(--text-secondary)"
                : "var(--text-primary)";
          return (
            <div
              key={r.label}
              title={r.title}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                padding: "6px 0",
                borderBottom: "1px dashed var(--border-subtle)",
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 9.5,
                  letterSpacing: "0.14em",
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                }}
              >
                {r.label}
              </span>
              <span
                style={{
                  fontFamily: r.mono ? "var(--font-mono)" : "var(--font-sans)",
                  fontSize: 13,
                  fontWeight: 500,
                  color,
                  letterSpacing: r.mono ? "0.04em" : "-0.005em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {r.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusBlock({ label, tone }: { label: string; tone?: "error" }) {
  return (
    <div
      style={{
        padding: "32px 24px",
        textAlign: "center",
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        color: tone === "error" ? "var(--error)" : "var(--text-muted)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
  );
}

function formatTerminationReason(reason: string): string {
  // Worker writes reasons like "agent_initiated:voicemail_detected" or
  // "halt:operator". Format the prefix:slug into a short label suitable
  // for a chip. Falls back to the raw string for unrecognized values.
  const map: Record<string, string> = {
    "agent_initiated:voicemail_detected": "VOICEMAIL",
    "agent_initiated:wrong_number": "WRONG NUMBER",
    "agent_initiated:target_unavailable": "UNAVAILABLE",
    "agent_initiated:target_refused": "REFUSED",
    "agent_initiated:objective_completed": "COMPLETED",
    stale_orphan_reaped: "REAPED",
  };
  if (reason in map) return map[reason];
  if (reason.startsWith("halt:")) return `HALT · ${reason.slice(5).toUpperCase()}`;
  if (reason.startsWith("agent_initiated:")) {
    return reason.slice("agent_initiated:".length).toUpperCase().replace(/_/g, " ");
  }
  return reason.toUpperCase().replace(/_/g, " ");
}

function Dot() {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 3,
        height: 3,
        background: "var(--text-muted)",
        margin: "0 10px 2px",
        verticalAlign: "middle",
      }}
    />
  );
}

// Card import kept for downstream pages that still reference it via the
// existing shared component. The new design uses bespoke side panels above.
void Card;
